import { z } from 'zod';
import { uuidSchema } from './validation.js';
/**
 * PO invoices + Xero accounting journals (BLU-456).
 *
 * Multiple supplier invoices per purchase order, AI invoice ingestion input,
 * and the per-org Xero settings shapes (organizations.settings -> 'xero').
 * Invoice line items are freehand-first: sku/description live on the line and
 * purchaseOrderItemId is an optional soft audit link.
 */
export const dateOnlySchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD');
/** Xero chart-of-accounts code, e.g. '330'. */
const accountCodeSchema = z.string().trim().min(1).max(20);
export const poInvoiceItemInputSchema = z.object({
    /** Optional soft link back to a PO line (auto-fill convenience / audit). */
    purchaseOrderItemId: uuidSchema.optional().nullable(),
    sku: z.string().trim().max(120).optional().nullable(),
    description: z.string().trim().max(1000).optional().nullable(),
    quantity: z.number().positive().max(99999999),
    unitCost: z.number().min(0).max(999999),
    taxRateId: uuidSchema.optional().nullable(),
    taxRate: z.number().min(0).max(1).optional(),
    accountCode: accountCodeSchema.optional().nullable(),
});
export const createPoInvoiceSchema = z.object({
    purchaseOrderId: uuidSchema,
    invoiceNumber: z.string().trim().min(1).max(120),
    invoiceDate: dateOnlySchema.optional().nullable(),
    dueDate: dateOnlySchema.optional().nullable(),
    paymentTerms: z.string().trim().max(200).optional().nullable(),
    isDeposit: z.boolean().default(false),
    notes: z.string().trim().max(2000).optional().nullable(),
    fileId: uuidSchema.optional().nullable(),
    items: z.array(poInvoiceItemInputSchema).max(200).default([]),
});
export const updatePoInvoiceSchema = z.object({
    invoiceId: uuidSchema,
    invoiceNumber: z.string().trim().min(1).max(120).optional(),
    invoiceDate: dateOnlySchema.optional().nullable(),
    dueDate: dateOnlySchema.optional().nullable(),
    paymentTerms: z.string().trim().max(200).optional().nullable(),
    notes: z.string().trim().max(2000).optional().nullable(),
    fileId: uuidSchema.optional().nullable(),
});
export const addPoInvoiceItemSchema = poInvoiceItemInputSchema.extend({
    invoiceId: uuidSchema,
});
export const updatePoInvoiceItemSchema = z.object({
    itemId: uuidSchema,
    sku: z.string().trim().max(120).optional().nullable(),
    description: z.string().trim().max(1000).optional().nullable(),
    quantity: z.number().positive().max(99999999).optional(),
    unitCost: z.number().min(0).max(999999).optional(),
    taxRateId: uuidSchema.optional().nullable(),
    taxRate: z.number().min(0).max(1).optional(),
    accountCode: accountCodeSchema.optional().nullable(),
});
/** fileId references an already-uploaded purchase_order_files row (PDF). */
export const analyzePoInvoiceSchema = z.object({
    purchaseOrderId: uuidSchema,
    fileId: uuidSchema,
});
// ---------------------------------------------------------------------------
// Per-org Xero settings (organizations.settings -> 'xero')
// ---------------------------------------------------------------------------
export const updateXeroCogsSettingsSchema = z.object({
    cogsEnabled: z.boolean().optional(),
    cogsAccountCode: accountCodeSchema.optional(),
    cogsInventoryAccountCode: accountCodeSchema.optional(),
    cogsStartDate: dateOnlySchema.optional().nullable(),
});
export const updateXeroSitSettingsSchema = z.object({
    sitReclassEnabled: z.boolean().optional(),
    sitInTransitAccountCode: accountCodeSchema.optional(),
    sitOnHandAccountCode: accountCodeSchema.optional(),
});
export const updateInvoiceAiSettingsSchema = z.object({
    invoiceAiEnabled: z.boolean(),
});
export const processCogsRangeSchema = z
    .object({
    startDate: dateOnlySchema.optional(),
    endDate: dateOnlySchema.optional(),
})
    .optional();
